import { useTranslation } from 'react-i18next'
import { CampaignCreateParams, List, SearchParams } from '../../../types'
import { SelectionProps } from '../../../ui/form/Field'
import { useCallback, useContext, useState } from 'react'
import { ProjectContext } from '../../../contexts'
import { useSearchTableState } from '../../../components'
import api from '../../../api'
import { useController } from 'react-hook-form'
import { snakeToTitle } from '../../../utils'
import { Button, DataTable, Heading, Modal } from '../../../ui'
import ListTable from '../../lists/ListTable'

interface CampaignListSelectionProps extends SelectionProps<CampaignCreateParams> {
    title: string
    subtitle?: string
    value?: List[]
    required: boolean
}

export const CampaignListSelection = ({
    control,
    name,
    title,
    subtitle,
    value,
    required,
}: CampaignListSelectionProps) => {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [isOpen, setIsOpen] = useState(false)
    const [lists, setLists] = useState<List[]>(value ?? [])
    const state = useSearchTableState(
        useCallback(async (params: SearchParams) => await api.lists.search(project.id, params), [project]),
        {
            filter: {
                state: ['ready', 'loading'],
            },
        },
    )
    const handlePickList = (list: List) => {
        if (list.state !== 'ready') {
            if (!confirm(t('campaign_list_generating'))) return
        }

        const newLists = [...lists.filter(item => item.id !== list.id), list]
        setLists(newLists)
        onChange(newLists.map(list => list.id))
        setIsOpen(false)
    }

    const handleRemoveList = (list: List) => {
        const newLists = [...lists].filter(item => item.id !== list.id)
        setLists(newLists)
        onChange(newLists.map(list => list.id))
    }

    const { field: { onChange } } = useController({
        control,
        name,
        rules: {
            required,
        },
    })

    return (
        <>
            <Heading size="h4" title={
                <>
                    {title}
                    {required && <span style={{ color: 'red' }}>&nbsp;*</span>}
                </>
            } actions={
                <Button
                    size="small"
                    onClick={() => setIsOpen(true)}>{t('add_list')}</Button>
            }>
                {subtitle}
            </Heading>
            <DataTable
                items={lists}
                itemKey={({ item }) => item.id}
                columns={[
                    { key: 'name', title: t('title') },
                    {
                        key: 'type',
                        title: t('type'),
                        cell: ({ item: { type } }) => snakeToTitle(type),
                    },
                    {
                        key: 'users_count',
                        title: t('users_count'),
                        cell: ({ item }) => item.users_count?.toLocaleString(),
                    },
                    { key: 'updated_at', title: t('updated_at') },
                    {
                        key: 'options',
                        title: t('options'),
                        cell: ({ item }) => (
                            <Button
                                size="small"
                                variant="destructive"
                                onClick={() => handleRemoveList(item)}>
                                {t('remove')}
                            </Button>
                        ),
                    },
                ]}
                emptyMessage={t('campaign_form_select_list')}
            />
            <Modal
                open={isOpen}
                onClose={setIsOpen}
                title={title}
                size="large">
                <ListTable
                    state={state}
                    onSelectRow={handlePickList}
                />
            </Modal>
        </>
    )
}
